import {pathGetExtension, Values} from '../';

export const MimeTypeString = {
    // Images
    ImageJpg: 'image/jpeg',
    ImagePng: 'image/png',
    ImageGif: 'image/gif',
    ImageWebp: 'image/webp',
    ImageSvg: 'image/svg+xml',
    ImageBmp: 'image/bmp',
    ImageTiff: 'image/tiff',
    ImageIcon: 'image/x-icon',
    ImageIconMicrosoft: 'image/vnd.microsoft.icon',
    ImageAvif: 'image/avif',
    ImageHeic: 'image/heic',
    ImageHeif: 'image/heif',
    ImageJp2: 'image/jp2',
    ImageCr2: 'image/x-canon-cr2',
    ImageNef: 'image/x-nikon-nef',
    ImageArw: 'image/x-sony-arw',
    ImageDng: 'image/x-adobe-dng',

    // Video
    VideoWebm: 'video/webm',
    VideoMp4: 'video/mp4',
    VideoMpeg: 'video/mpeg',
    VideoOgg: 'video/ogg',
    VideoQuicktime: 'video/quicktime',
    VideoAvi: 'video/x-msvideo',
    VideoDvd: 'video/dvd',
    VideoM4v: 'video/x-m4v',
    Video3gpp: 'video/3gpp',
    VideoMatroska: 'video/x-matroska',
    VideoDivx: 'video/divx',
    VideoXvid: 'video/x-xvid',
    VideoWmv: 'video/x-ms-wmv',
    VideoFlv: 'video/x-flv',
    VideoMpeg2: 'video/mp2t',

    // Audio
    AudioMp3: 'audio/mpeg',
    AudioOgg: 'audio/ogg',
    AudioWav: 'audio/wav',
    AudioWebm: 'audio/webm',
    AudioAac: 'audio/aac',
    AudioFlac: 'audio/flac',
    AudioM4a: 'audio/mp4',
    AudioWma: 'audio/x-ms-wma',
    AudioAiff: 'audio/aiff',

    // Documents
    PDF: 'application/pdf',
    Doc: 'application/msword',
    Docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    DocxMacroEnabled: 'application/vnd.ms-word.document.macroEnabled.12',
    Docm: 'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    Xls: 'application/vnd.ms-excel',
    Xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    Xltx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    Ppt: 'application/vnd.ms-powerpoint',
    Pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    Potx: 'application/vnd.openxmlformats-officedocument.presentationml.template',
    Ppsx: 'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
    Rtf: 'application/rtf',

    // Archives
    Zip: 'application/zip',
    Tar: 'application/x-tar',
    Rar: 'application/x-rar-compressed',
    RarVnd: 'application/vnd.rar',
    SevenZip: 'application/x-7z-compressed',
    Gzip: 'application/gzip',
    Jar: 'application/java-archive',
    WebArchive: 'application/x-webarchive',

    // Web
    HTML: 'text/html',
    CSS: 'text/css',
    JavaScript: 'text/javascript',
    XML: 'application/xml',
    TextXml: 'text/xml',

    // Text
    TextPlain: 'text/plain',
    CSV: 'text/csv',
    Markdown: 'text/markdown',
    VCF: 'text/vcard',

    // Data
    JSON: 'application/json',
    YAML: 'application/x-yaml',

    // 3D Models
    Glb: 'model/gltf-binary',

    // Other Media Formats
    Mxf: 'application/mxf',

    // Other
    OctetStream: 'application/octet-stream',
    FormData: 'multipart/form-data',
    FormUrlEncoded: 'application/x-www-form-urlencoded',
} as const;

export type MimeTypeString = Values<typeof MimeTypeString>;

const extensionsToMimeType = new Map([
    // Images
    ['.jpg', MimeTypeString.ImageJpg],
    ['.jpeg', MimeTypeString.ImageJpg],
    ['.png', MimeTypeString.ImagePng],
    ['.gif', MimeTypeString.ImageGif],
    ['.webp', MimeTypeString.ImageWebp],
    ['.svg', MimeTypeString.ImageSvg],
    ['.bmp', MimeTypeString.ImageBmp],
    ['.tiff', MimeTypeString.ImageTiff],
    ['.tif', MimeTypeString.ImageTiff],
    ['.ico', MimeTypeString.ImageIcon],
    ['.avif', MimeTypeString.ImageAvif],
    ['.heic', MimeTypeString.ImageHeic],
    ['.heif', MimeTypeString.ImageHeif],
    ['.jp2', MimeTypeString.ImageJp2],
    ['.cr2', MimeTypeString.ImageCr2],
    ['.nef', MimeTypeString.ImageNef],
    ['.arw', MimeTypeString.ImageArw],
    ['.dng', MimeTypeString.ImageDng],

    // Video
    ['.webm', MimeTypeString.VideoWebm],
    ['.mp4', MimeTypeString.VideoMp4],
    ['.mpeg', MimeTypeString.VideoMpeg],
    ['.mpg', MimeTypeString.VideoMpeg],
    ['.ogv', MimeTypeString.VideoOgg],
    ['.mov', MimeTypeString.VideoQuicktime],
    ['.avi', MimeTypeString.VideoAvi],
    ['.m4v', MimeTypeString.VideoM4v],
    ['.3gp', MimeTypeString.Video3gpp],
    ['.3gpp', MimeTypeString.Video3gpp],
    ['.mkv', MimeTypeString.VideoMatroska],
    ['.divx', MimeTypeString.VideoDivx],
    ['.xvid', MimeTypeString.VideoXvid],
    ['.wmv', MimeTypeString.VideoWmv],
    ['.flv', MimeTypeString.VideoFlv],
    ['.ts', MimeTypeString.VideoMpeg2],
    ['.mxf', MimeTypeString.Mxf],

    // Audio
    ['.mp3', MimeTypeString.AudioMp3],
    ['.ogg', MimeTypeString.AudioOgg],
    ['.oga', MimeTypeString.AudioOgg],
    ['.wav', MimeTypeString.AudioWav],
    ['.weba', MimeTypeString.AudioWebm],
    ['.aac', MimeTypeString.AudioAac],
    ['.flac', MimeTypeString.AudioFlac],
    ['.m4a', MimeTypeString.AudioM4a],
    ['.wma', MimeTypeString.AudioWma],
    ['.aiff', MimeTypeString.AudioAiff],
    ['.aif', MimeTypeString.AudioAiff],

    // Documents
    ['.pdf', MimeTypeString.PDF],
    ['.doc', MimeTypeString.Doc],
    ['.docx', MimeTypeString.Docx],
    ['.docm', MimeTypeString.DocxMacroEnabled],
    ['.dotx', MimeTypeString.Docm],
    ['.xls', MimeTypeString.Xls],
    ['.xlsx', MimeTypeString.Xlsx],
    ['.xltx', MimeTypeString.Xltx],
    ['.ppt', MimeTypeString.Ppt],
    ['.pptx', MimeTypeString.Pptx],
    ['.potx', MimeTypeString.Potx],
    ['.ppsx', MimeTypeString.Ppsx],
    ['.rtf', MimeTypeString.Rtf],

    // Archives
    ['.zip', MimeTypeString.Zip],
    ['.tar', MimeTypeString.Tar],
    ['.rar', MimeTypeString.Rar],
    ['.7z', MimeTypeString.SevenZip],
    ['.gz', MimeTypeString.Gzip],
    ['.jar', MimeTypeString.Jar],
    ['.webarchive', MimeTypeString.WebArchive],

    // Web
    ['.html', MimeTypeString.HTML],
    ['.htm', MimeTypeString.HTML],
    ['.css', MimeTypeString.CSS],
    ['.js', MimeTypeString.JavaScript],
    ['.mjs', MimeTypeString.JavaScript],
    ['.xml', MimeTypeString.XML],

    // Text
    ['.txt', MimeTypeString.TextPlain],
    ['.csv', MimeTypeString.CSV],
    ['.md', MimeTypeString.Markdown],
    ['.vcf', MimeTypeString.VCF],

    // Data
    ['.json', MimeTypeString.JSON],
    ['.yaml', MimeTypeString.YAML],
    ['.yml', MimeTypeString.YAML],

    // 3D Models
    ['.glb', MimeTypeString.Glb],
])

export function mimeTypeResolveFromPath (path: string) : MimeTypeString | undefined {
    const extension = pathGetExtension(path);

    return extensionsToMimeType.get(extension);
}

export const MimeTypeTopLevelTypes = {
    Audio: 'audio',
    Video: 'video',
    Image: 'image',
    Text: 'text',
    Application: 'application',
    Multipart: 'multipart',
    Message: 'message',
    Model: 'model',
} as const;

export type MimeTypeTopLevelTypes = Values<typeof MimeTypeTopLevelTypes>;